import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Camera, Check, ChevronLeft, ChevronRight, ClipboardCheck, Minus, Plus, RotateCcw, Search, Tag, Trash2, X } from 'lucide-react';

import {
  useBulkDeleteEmpty, useConfirmBottle, useCountries, useEmptyStockPreview,
  useRemoveBottle, useResetInventoryCheck, useSetKeeper, useWineries, useWines,
  type RemovalReason,
} from './useWines';
import { GroupedWineCard, groupWines } from './GroupedWineCard';
import { GroupedWineTable } from './GroupedWineTable';
import { cn } from '@/lib/cn';
import { fmtUsd, fmtInt } from '@/lib/format';
import { useIsMobile } from '@/lib/useIsMobile';
import type { WineFilters } from './types';

type Tab = 'all' | 'keeper' | 'for_sale' | 'unboxed' | 'no_stock';
type BalanceMode = 'off' | 'on';

// Lee filtros desde los query params de la URL para que persistan al hacer
// back, refresh o compartir el link.
function readFiltersFromUrl(sp: URLSearchParams): { tab: Tab; filters: WineFilters } {
  const filters: WineFilters = {
    per_page: sp.get('per_page') ? Number(sp.get('per_page')) : 25,
    page: sp.get('page') ? Number(sp.get('page')) : 1,
  };
  if (sp.get('q')) filters.q = sp.get('q')!;
  if (sp.get('country_id')) filters.country_id = Number(sp.get('country_id'));
  if (sp.get('winery_id')) filters.winery_id = Number(sp.get('winery_id'));
  if (sp.get('type')) filters.type = sp.get('type')!;
  if (sp.get('vintage_min')) filters.vintage_min = Number(sp.get('vintage_min'));
  if (sp.get('vintage_max')) filters.vintage_max = Number(sp.get('vintage_max'));

  const tabParam = sp.get('tab');
  const tab: Tab =
    tabParam === 'keeper' || tabParam === 'for_sale' || tabParam === 'unboxed' || tabParam === 'no_stock'
      ? tabParam
      : 'all';
  return { tab, filters };
}

export function WinesListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Inicializar desde la URL si hay query params.
  const initial = useMemo(() => readFiltersFromUrl(searchParams), []);
  const [tab, setTab] = useState<Tab>(initial.tab);
  const [filters, setFilters] = useState<WineFilters>(initial.filters);
  const isMobile = useIsMobile();

  // Sincronizar tab + filters → URL (replace para no spamear el history).
  useEffect(() => {
    const next: Record<string, string> = {};
    if (tab !== 'all') next.tab = tab;
    if (filters.q) next.q = filters.q;
    if (filters.country_id) next.country_id = String(filters.country_id);
    if (filters.winery_id) next.winery_id = String(filters.winery_id);
    if (filters.type) next.type = filters.type;
    if (filters.vintage_min) next.vintage_min = String(filters.vintage_min);
    if (filters.vintage_max) next.vintage_max = String(filters.vintage_max);
    if (filters.page && filters.page > 1) next.page = String(filters.page);
    if (filters.per_page && filters.per_page !== 25) next.per_page = String(filters.per_page);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, filters]);

  // Modo balance: activa el panel de check + cuenta pendientes.
  const [balance, setBalance] = useState<BalanceMode>('off');
  const [showOnlyPending, setShowOnlyPending] = useState(false);

  // La tab define el filtro is_keeper que viaja al backend.
  // En mobile traemos hasta 500 para poder agrupar correctamente por (winery,name,format).
  const effectiveFilters: WineFilters = useMemo(() => {
    const base = { ...filters };
    delete base.is_keeper;
    delete base.for_sale_max_usd;
    if (isMobile) {
      base.per_page = 500;
      base.page = 1;
    }
    if (tab === 'keeper') return { ...base, is_keeper: true };
    if (tab === 'for_sale') return { ...base, is_keeper: false };
    // "Sin caja": no aplica is_keeper. Muestra todos los vinos con al menos
    // una botella sin asignar a una caja física.
    if (tab === 'unboxed') return { ...base, in_box: 'unboxed' as const };
    // "Sin stock": vinos con stock = 0 (consumidos, mal cargados, por comprar).
    if (tab === 'no_stock') return { ...base, has_stock: 'no' as const };
    return base;
  }, [filters, tab, isMobile]);

  // Filtro adicional: cuando el balance está activo y "solo pendientes" tildado.
  const filtersWithCheck = useMemo(() => {
    if (balance === 'on' && showOnlyPending) {
      return { ...effectiveFilters, inventory_check: 'pending' as const };
    }
    return effectiveFilters;
  }, [effectiveFilters, balance, showOnlyPending]);

  const { data: wineries } = useWineries();
  const { data: countries } = useCountries();
  const { data, isLoading } = useWines(filtersWithCheck);
  const setKeeper = useSetKeeper();
  const confirmBottle = useConfirmBottle();
  const removeBottle = useRemoveBottle();
  const resetCheck = useResetInventoryCheck();
  const emptyPreview = useEmptyStockPreview();
  const bulkDeleteEmpty = useBulkDeleteEmpty();

  const cleanupEmpty = async () => {
    const preview = await emptyPreview.mutateAsync();
    if (preview.count === 0) {
      alert('No hay vinos sin stock para eliminar.');
      return;
    }
    const list = preview.sample.map((n) => `• ${n}`).join('\n')
      + (preview.count > preview.sample.length ? `\n… y ${preview.count - preview.sample.length} más` : '');
    if (!confirm(`Se van a eliminar ${preview.count} vino(s) con stock 0.\n\nEjemplos:\n${list}\n\n¿Continuar? (soft-delete, se pueden restaurar por consola)`)) return;
    const res = await bulkDeleteEmpty.mutateAsync();
    alert(`Eliminados: ${res.deleted_count} vino(s).`);
  };

  // Vino al que se le está dando de baja una botella (abre modal de razón).
  const [removingWineId, setRemovingWineId] = useState<number | null>(null);

  // Vista agrupada por (bodega, nombre, formato) o lista plana por añada.
  // En mobile mantiene el layout de cards agrupadas; el toggle solo afecta desktop.
  // El modo balance fuerza lista plana para poder chequear una por una.
  const [groupedDesktop, setGroupedDesktop] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return localStorage.getItem('wines_grouped_desktop') !== '0';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem('wines_grouped_desktop', groupedDesktop ? '1' : '0');
  }, [groupedDesktop]);

  // Agrupamos si mobile, o si desktop+groupedDesktop.
  const showGrouped = balance === 'off' && (isMobile || groupedDesktop);
  const groups = useMemo(() => {
    if (!showGrouped || !data?.data) return [];
    return groupWines(data.data);
  }, [showGrouped, data?.data]);

  const total = data?.meta?.total ?? 0;
  const totalStock = (data?.meta as any)?.total_stock ?? 0;
  const totalEstimatedUsd = (data?.meta as any)?.total_estimated_usd ?? null;
  const currentPage = data?.meta?.current_page ?? filters.page ?? 1;
  const lastPage = data?.meta?.last_page ?? 1;
  const perPage = data?.meta?.per_page ?? filters.per_page ?? 25;
  const fromRow = total === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const toRow = Math.min(currentPage * perPage, total);
  const wines = useMemo(() => data?.data ?? [], [data]);

  const update = (patch: Partial<WineFilters>) =>
    setFilters((f) => ({ ...f, page: 1, ...patch }));

  const goToPage = (page: number) =>
    setFilters((f) => ({ ...f, page: Math.max(1, Math.min(lastPage, page)) }));

  const switchTab = (next: Tab) => {
    setTab(next);
    setFilters((f) => ({ ...f, page: 1 }));
  };

  const toggleKeeper = (id: number, current: boolean) => {
    setKeeper.mutate({ id, isKeeper: !current });
  };

  const tabLabel =
    tab === 'keeper'
      ? 'en guarda'
      : tab === 'for_sale'
      ? 'en venta'
      : tab === 'unboxed'
      ? 'sin caja'
      : tab === 'no_stock'
      ? 'sin stock'
      : 'en tu cava';

  return (
    <div className="p-8">
      <header className="mb-4 md:mb-6">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-medium">Mis vinos</h1>
            <p className="text-xs md:text-sm text-notion-500 hidden md:block">
              Gestión de la cava
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={cleanupEmpty}
              disabled={emptyPreview.isPending || bulkDeleteEmpty.isPending}
              className="btn-secondary text-rose-700 border-rose-200 hover:bg-rose-50 hidden md:inline-flex"
              title="Eliminar todos los vinos con stock 0 (soft-delete)"
            >
              <Trash2 size={14} />
              <span className="hidden lg:inline">Sin stock</span>
            </button>
            <Link to="/wines/new" className="btn-primary">
              <Plus size={16} />
              <span className="hidden sm:inline">Nuevo vino</span>
            </Link>
          </div>
        </div>

        {/* KPIs — grid en mobile (3 columnas compactas), inline en desktop */}
        <div className="grid grid-cols-3 gap-2 md:flex md:items-center md:gap-3 md:justify-end">
          <div className="md:text-right md:pr-3 md:border-r md:border-notion-200">
            <p className="text-[10px] md:text-[11px] uppercase tracking-wide text-notion-500">
              Vinos
            </p>
            <p className="text-base md:text-lg font-medium text-notion-800 tabular-nums">
              {fmtInt(total)}
            </p>
            <p className="text-[10px] text-notion-400 md:hidden">{tabLabel}</p>
          </div>
          <div className="md:text-right md:pr-3 md:border-r md:border-notion-200">
            <p className="text-[10px] md:text-[11px] uppercase tracking-wide text-notion-500">
              Botellas
            </p>
            <p className="text-base md:text-lg font-medium text-notion-800 tabular-nums">
              {fmtInt(totalStock)}
            </p>
          </div>
          <div className="md:text-right md:pr-3 md:border-r md:border-notion-200">
            <p className="text-[10px] md:text-[11px] uppercase tracking-wide text-notion-500">
              Valor USD
            </p>
            <p className="text-base md:text-lg font-medium text-notion-800 tabular-nums">
              {totalEstimatedUsd && totalEstimatedUsd > 0
                ? fmtUsd(totalEstimatedUsd)
                : '—'}
            </p>
          </div>
          {balance === 'on' && (
            <div className="col-span-3 md:col-span-1 md:text-right md:pr-3 md:border-r md:border-notion-200">
              <p className="text-[10px] md:text-[11px] uppercase tracking-wide text-emerald-700">
                Botellas confirmadas
              </p>
              <p className="text-base md:text-lg font-medium text-emerald-700 tabular-nums">
                {fmtInt(wines.reduce((s, w) => s + (w.bottles_confirmed ?? 0), 0))} /{' '}
                {fmtInt(wines.reduce((s, w) => s + (w.bottles_total ?? 0), 0))}
              </p>
            </div>
          )}
        </div>
      </header>

      {/* Tabs */}
      <div className="flex items-center border-b border-notion-200 mb-4 overflow-x-auto">
        <TabButton active={tab === 'all'} onClick={() => switchTab('all')}>
          Todos
        </TabButton>
        <TabButton active={tab === 'keeper'} onClick={() => switchTab('keeper')}>
          Guarda
        </TabButton>
        <TabButton active={tab === 'for_sale'} onClick={() => switchTab('for_sale')}>
          En venta
        </TabButton>
        <TabButton active={tab === 'unboxed'} onClick={() => switchTab('unboxed')}>
          Sin caja
        </TabButton>
        <TabButton active={tab === 'no_stock'} onClick={() => switchTab('no_stock')}>
          Sin stock
        </TabButton>
        {/* Toggle vista agrupada / lista plana — solo desktop y solo fuera del balance */}
        {balance === 'off' && (
          <button
            type="button"
            onClick={() => setGroupedDesktop((v) => !v)}
            className="ml-auto hidden md:inline-flex px-3 py-1.5 text-xs font-medium rounded-md items-center gap-1.5 transition mb-1.5 bg-notion-100 text-notion-700 hover:bg-notion-200"
            title="Alternar entre vista agrupada por vino (con añadas desplegables) y lista plana por añada"
          >
            {groupedDesktop ? 'Vista: Agrupada' : 'Vista: Añada individual'}
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            setBalance((b) => (b === 'on' ? 'off' : 'on'));
            setShowOnlyPending(false);
          }}
          className={cn(
            'px-3 py-1.5 text-xs font-medium rounded-md inline-flex items-center gap-1.5 transition mb-1.5',
            balance === 'off' ? 'md:ml-2' : 'ml-auto',
            balance === 'on'
              ? 'bg-emerald-600 text-white hover:bg-emerald-700'
              : 'bg-notion-100 text-notion-700 hover:bg-notion-200',
          )}
        >
          <ClipboardCheck size={12} />
          {balance === 'on' ? 'Balance activo' : 'Iniciar balance'}
        </button>
      </div>

      {/* Panel de balance — solo cuando está activo */}
      {balance === 'on' && (
        <div className="card p-3 mb-4 bg-emerald-50 border-emerald-200 flex flex-wrap items-center gap-3">
          <div className="text-sm">
            <strong className="text-emerald-900">Modo balance</strong>
            <span className="text-notion-600 ml-2">
              Por cada vino: <strong>+</strong> confirma una botella, <strong>−</strong> registra una faltante.
            </span>
          </div>
          <label className="text-xs inline-flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={showOnlyPending}
              onChange={(e) => setShowOnlyPending(e.target.checked)}
              className="w-3.5 h-3.5"
            />
            Mostrar solo pendientes
          </label>
          <button
            type="button"
            onClick={() => {
              if (confirm('¿Iniciar balance nuevo? Se vuelven a pendiente todas las botellas en cava (no se pierde stock).')) {
                resetCheck.mutate();
              }
            }}
            disabled={resetCheck.isPending}
            className="text-xs px-2 py-1 rounded inline-flex items-center gap-1.5 bg-white border border-notion-200 hover:border-notion-400 text-notion-700"
          >
            <RotateCcw size={11} />
            {resetCheck.isPending ? 'Reseteando…' : 'Iniciar balance nuevo'}
          </button>
        </div>
      )}

      <div className="card p-4 mb-4 grid grid-cols-1 md:grid-cols-6 gap-3">
        <div className="md:col-span-2">
          <label className="label">Buscar</label>
          <div className="relative">
            <Search size={16} className="absolute top-1/2 left-3 -translate-y-1/2 text-notion-500" />
            <input
              className="input pl-9"
              placeholder="Nombre del vino"
              value={filters.q ?? ''}
              onChange={(e) => update({ q: e.target.value || undefined })}
            />
          </div>
        </div>
        <div>
          <label className="label">País</label>
          <select
            className="input"
            value={filters.country_id ?? ''}
            onChange={(e) =>
              update({ country_id: e.target.value ? Number(e.target.value) : undefined })
            }
          >
            <option value="">Todos</option>
            {countries
              ?.slice()
              .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }))
              .map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
          </select>
        </div>
        <div>
          <label className="label">Bodega</label>
          <select
            className="input"
            value={filters.winery_id ?? ''}
            onChange={(e) => update({ winery_id: e.target.value ? Number(e.target.value) : undefined })}
          >
            <option value="">Todas</option>
            {wineries
              ?.slice()
              .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }))
              .map((w) => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
          </select>
        </div>
        <div>
          <label className="label">Tipo</label>
          <select
            className="input"
            value={filters.type ?? ''}
            onChange={(e) => update({ type: e.target.value || undefined })}
          >
            <option value="">Todos</option>
            <option value="tinto">Tinto</option>
            <option value="blanco">Blanco</option>
            <option value="rosado">Rosado</option>
            <option value="espumante">Espumante</option>
            <option value="dulce">Dulce</option>
            <option value="fortificado">Fortificado</option>
          </select>
        </div>
        <div>
          <label className="label">Añada (rango)</label>
          <div className="flex gap-2">
            <input
              type="number"
              className="input"
              placeholder="desde"
              value={filters.vintage_min ?? ''}
              onChange={(e) => update({ vintage_min: e.target.value ? Number(e.target.value) : undefined })}
            />
            <input
              type="number"
              className="input"
              placeholder="hasta"
              value={filters.vintage_max ?? ''}
              onChange={(e) => update({ vintage_max: e.target.value ? Number(e.target.value) : undefined })}
            />
          </div>
        </div>
      </div>

      {/* Listado: tabla en desktop, cards apilables en mobile */}
      {isLoading ? (
        <div className="card p-12 text-center text-notion-500">Cargando…</div>
      ) : wines.length === 0 ? (
        <div className="card p-12 text-center text-notion-500">
          {tab === 'for_sale'
            ? 'No hay vinos marcados como En venta. Cambiá un vino desde el botón verde "Guarda" para moverlo acá.'
            : tab === 'keeper'
            ? 'No hay vinos en Guarda.'
            : tab === 'unboxed'
            ? '¡Todos los vinos con stock están asignados a cajas físicas!'
            : tab === 'no_stock'
            ? '¡No hay vinos sin stock en tu cava! Podés usar "Sin stock" (arriba a la derecha) para limpiar los que sí queden.'
            : 'No hay vinos que coincidan con tus filtros.'}
        </div>
      ) : (
        <>
          {/* DESKTOP: vista agrupada cuando corresponde */}
          {balance === 'off' && groupedDesktop && (
            <GroupedWineTable
              groups={groups}
              wines={wines}
              onToggleKeeper={toggleKeeper}
              keeperPending={setKeeper.isPending}
            />
          )}

          {/* DESKTOP: tabla clásica (lista plana o modo balance) */}
          {(balance === 'on' || !groupedDesktop) && (
          <div className="card overflow-x-auto hidden md:block">
            <table className="table-base min-w-[960px]">
              <thead className="bg-white">
                <tr>
                  {balance === 'on' && <th className="w-40">Balance</th>}
                  <th>Vino</th>
                  <th>Bodega</th>
                  <th>Añada</th>
                  <th>Tipo</th>
                  <th>Formato</th>
                  <th>Stock</th>
                  <th className="text-center">Fotos</th>
                  <th>Costo prom. USD</th>
                  <th>Valor est. USD</th>
                  <th className="text-center">Guarda</th>
                </tr>
              </thead>
              <tbody>
                {wines.map((w) => {
                  const allConfirmed =
                    (w.bottles_total ?? 0) > 0 &&
                    (w.bottles_confirmed ?? 0) >= (w.bottles_total ?? 0);
                  return (
                    <tr key={w.id} className={cn('hover:bg-white', balance === 'on' && allConfirmed && 'bg-emerald-50/40')}>
                      {balance === 'on' && (
                        <td>
                          <BottleBalanceCounter
                            total={w.bottles_total ?? 0}
                            confirmed={w.bottles_confirmed ?? 0}
                            onConfirm={() => confirmBottle.mutate({ id: w.id })}
                            onRemove={() => setRemovingWineId(w.id)}
                            busy={confirmBottle.isPending || removeBottle.isPending}
                          />
                        </td>
                      )}
                      <td>
                        <Link to={`/wines/${w.id}`} className="font-medium text-notion-800 hover:underline">
                          {w.name}
                        </Link>
                        {w.grape_varieties?.length ? (
                          <p className="text-xs text-notion-500 mt-1">
                            {w.grape_varieties.map((g) => g.name).join(' · ')}
                          </p>
                        ) : null}
                      </td>
                      <td>{w.winery?.name}</td>
                      <td>{w.vintage ?? '—'}</td>
                      <td><span className="capitalize">{w.type_label}</span></td>
                      <td><span className="text-xs text-notion-600">{w.format ?? '—'}</span></td>
                      <td>{w.current_stock ?? '—'}</td>
                      <td className="text-center">
                        <PhotosBadge count={w.images_count ?? 0} />
                      </td>
                      <td>{fmtUsd(w.avg_cost_usd ?? null)}</td>
                      <td>{fmtUsd(w.estimated_value_usd ?? null)}</td>
                      <td className="text-center">
                        <KeeperToggle
                          isKeeper={w.is_keeper}
                          disabled={setKeeper.isPending}
                          onToggle={() => toggleKeeper(w.id, w.is_keeper)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}

          {/* MOBILE: cards agrupadas por defecto. En modo balance pasamos a lista plana
              con contador por vino para verificación botella a botella. */}
          {balance === 'on' ? (
            <ul className="md:hidden space-y-2">
              {wines.map((w) => {
                const total = w.bottles_total ?? 0;
                const confirmed = w.bottles_confirmed ?? 0;
                const allConfirmed = total > 0 && confirmed >= total;
                return (
                  <li
                    key={w.id}
                    className={cn(
                      'card p-3 flex items-center gap-3',
                      allConfirmed && 'bg-emerald-50/50 border-emerald-200',
                    )}
                  >
                    <Link to={`/wines/${w.id}`} className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-notion-800 truncate">
                        {w.name}
                      </p>
                      <p className="text-[11px] text-notion-500 truncate flex items-center gap-1.5">
                        <span className="truncate">
                          {w.winery?.name ?? '—'} · {w.vintage ?? 's/a'} · {w.format}
                        </span>
                        {(w.images_count ?? 0) > 0 && (
                          <span className="shrink-0 inline-flex items-center gap-0.5 text-notion-500">
                            <Camera size={10} /> {w.images_count}
                          </span>
                        )}
                      </p>
                    </Link>
                    <BottleBalanceCounter
                      total={total}
                      confirmed={confirmed}
                      onConfirm={() => confirmBottle.mutate({ id: w.id })}
                      onRemove={() => setRemovingWineId(w.id)}
                      busy={confirmBottle.isPending || removeBottle.isPending}
                    />
                  </li>
                );
              })}
            </ul>
          ) : (
            <ul className="md:hidden space-y-2">
              {groups.map((g) => (
                <GroupedWineCard key={g.key} group={g} />
              ))}
            </ul>
          )}
        </>
      )}

      {/* Modal de razón de baja */}
      {removingWineId !== null && (
        <RemovalReasonModal
          onCancel={() => setRemovingWineId(null)}
          onPick={(reason) => {
            removeBottle.mutate(
              { id: removingWineId, reason },
              { onSettled: () => setRemovingWineId(null) },
            );
          }}
          submitting={removeBottle.isPending}
        />
      )}

      {total > 0 && (
        <div className="hidden md:flex items-center justify-between mt-4 text-sm text-notion-500">
          <div>
            Mostrando <strong className="text-notion-800">{fmtInt(fromRow)}</strong>–
            <strong className="text-notion-800">{fmtInt(toRow)}</strong> de{' '}
            <strong className="text-notion-800">{fmtInt(total)}</strong>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2">
              Por página:
              <select
                className="input py-1 px-2 text-sm"
                value={perPage}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, per_page: Number(e.target.value), page: 1 }))
                }
              >
                {[25, 50, 100, 200].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage <= 1}
              className="btn-secondary disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Página anterior"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="px-2">
              Página <strong className="text-notion-800">{currentPage}</strong> de{' '}
              <strong className="text-notion-800">{lastPage}</strong>
            </span>
            <button
              type="button"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage >= lastPage}
              className="btn-secondary disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Página siguiente"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-3 py-2 text-sm border-b-2 -mb-px transition-colors',
        active
          ? 'border-notion-800 text-notion-800 font-medium'
          : 'border-transparent text-notion-500 hover:text-notion-800',
      )}
    >
      {children}
    </button>
  );
}

/**
 * Contador de balance por vino: muestra "X / Y" con dos botones grandes:
 *   [−] (abre modal de razón de baja)
 *   [+] (confirma una botella pendiente)
 *
 * Cuando confirmed === total, muestra check verde y deshabilita el [+].
 * Si total === 0, no muestra nada (vino sin stock).
 */
function BottleBalanceCounter({
  total,
  confirmed,
  onConfirm,
  onRemove,
  busy,
}: {
  total: number;
  confirmed: number;
  onConfirm: () => void;
  onRemove: () => void;
  busy?: boolean;
}) {
  if (total === 0) {
    return <span className="text-[11px] text-notion-400">sin stock</span>;
  }
  const allDone = confirmed >= total;
  return (
    <div className="inline-flex items-center gap-1">
      <button
        type="button"
        onClick={onRemove}
        disabled={busy}
        className="w-8 h-8 rounded-md border border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-700 inline-flex items-center justify-center disabled:opacity-40"
        aria-label="Marcar botella como faltante"
        title="Falta una"
      >
        <Minus size={14} strokeWidth={2.5} />
      </button>
      <div
        className={cn(
          'min-w-[3.5rem] px-2 h-8 rounded-md border inline-flex items-center justify-center text-xs font-medium tabular-nums gap-1',
          allDone
            ? 'bg-emerald-600 border-emerald-700 text-white'
            : 'bg-white border-notion-200 text-notion-800',
        )}
      >
        {allDone && <Check size={12} strokeWidth={3} />}
        <span>{confirmed} / {total}</span>
      </div>
      <button
        type="button"
        onClick={onConfirm}
        disabled={busy || allDone}
        className="w-8 h-8 rounded-md border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 inline-flex items-center justify-center disabled:opacity-40"
        aria-label="Confirmar una botella"
        title="Confirmar una"
      >
        <Plus size={14} strokeWidth={2.5} />
      </button>
    </div>
  );
}

/**
 * Modal que aparece al apretar [−] en el balance: pregunta por qué falta la botella.
 * Las tres razones (consumo / regalo / merma) generan una baja con el motivo correcto.
 */
function RemovalReasonModal({
  onCancel,
  onPick,
  submitting,
}: {
  onCancel: () => void;
  onPick: (reason: RemovalReason) => void;
  submitting?: boolean;
}) {
  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-2xl p-4 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2 mb-3">
          <div>
            <p className="font-medium text-notion-800">¿Qué pasó con esa botella?</p>
            <p className="text-xs text-notion-500">Elegí la razón — se descuenta del stock.</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="text-notion-500 hover:text-notion-800"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>
        <div className="grid grid-cols-1 gap-2">
          <ReasonButton onClick={() => onPick('consumption')} disabled={submitting} label="Consumo" hint="La tomé" tone="amber" />
          <ReasonButton onClick={() => onPick('gift')} disabled={submitting} label="Regalo" hint="La regalé" tone="sky" />
          <ReasonButton onClick={() => onPick('loss')} disabled={submitting} label="Merma" hint="Rota / desaparecida" tone="rose" />
        </div>
      </div>
    </div>
  );
}

function ReasonButton({
  onClick,
  disabled,
  label,
  hint,
  tone,
}: {
  onClick: () => void;
  disabled?: boolean;
  label: string;
  hint: string;
  tone: 'amber' | 'sky' | 'rose';
}) {
  const tones: Record<string, string> = {
    amber: 'bg-amber-50 border-amber-200 hover:bg-amber-100 text-amber-900',
    sky: 'bg-sky-50 border-sky-200 hover:bg-sky-100 text-sky-900',
    rose: 'bg-rose-50 border-rose-200 hover:bg-rose-100 text-rose-900',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'w-full p-3 rounded-lg border text-left transition disabled:opacity-50',
        tones[tone],
      )}
    >
      <p className="font-medium">{label}</p>
      <p className="text-xs opacity-80">{hint}</p>
    </button>
  );
}

/** Pequeño badge con ícono de cámara y conteo de fotos del vino. */
function PhotosBadge({ count }: { count: number }) {
  if (count === 0) {
    return <span className="text-notion-300 text-xs">—</span>;
  }
  return (
    <span
      className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-notion-50 border border-notion-200 text-notion-700"
      title={`${count} foto${count === 1 ? '' : 's'}`}
    >
      <Camera size={11} />
      {count}
    </span>
  );
}

function KeeperToggle({
  isKeeper,
  disabled,
  onToggle,
}: {
  isKeeper: boolean;
  disabled?: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      className={cn(
        'inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md border transition-colors',
        isKeeper
          ? 'bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100'
          : 'bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100',
        disabled && 'opacity-50 cursor-not-allowed',
      )}
      title={isKeeper ? 'Click para marcar como En venta' : 'Click para marcar como Guarda'}
    >
      <Tag size={12} />
      {isKeeper ? 'Guarda' : 'En venta'}
    </button>
  );
}
